import React from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';

import IInstallation from '../../IInstallation';
import { getMarginClass } from '../getMarginRatioClassName';

const InstallationsSum = ({
  installations,
  quantity,
}: {
  installations: IInstallation[];
  quantity: number;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);

  const expensePerUnit = getExpensePerUnit(installations, []);
  const expenseTotal = getExpenseTotal(installations, [], quantity);
  const contributionMarginRatio = getContributionMarginRatio(installations, []);
  const sellingPriceForUnit = getSellingPricePerUnit(installations, []);
  const sellingPriceTotal = getSellingPriceTotal(installations, [], quantity);

  // The margins shown: the shown selling price minus the shown expense, so they add up
  const contributionMarginPerUnit = getShownDifference(
    sellingPriceForUnit,
    expensePerUnit
  );

  const contributionMarginTotal = getShownDifference(
    sellingPriceTotal,
    expenseTotal
  );

  return (
    <Row className="calculation-sum-row">
      <Col xs="4" />
      {/* The selling price and the margins green, as on the rows */}
      <Col className="sum-figure">
        <NumberWithUnitInputField
          disabled
          label={t(`${translations.offers}:Selling price`)}
          unit="€"
          value={formatGroupedNumber(sellingPriceForUnit)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={t(`${translations.offers}:Expense`)}
          unit="€"
          value={formatGroupedNumber(expensePerUnit)}
        />
      </Col>
      <Col className="sum-figure">
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Contribution margin`)} €`}
          unit="€"
          value={formatGroupedNumber(contributionMarginPerUnit)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Selling price`)} ${t(
            `${translations.common}:total-short`
          )}`}
          unit="€"
          value={formatGroupedNumber(sellingPriceTotal)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Expense`)} ${t(
            `${translations.common}:total-short`
          )}`}
          unit="€"
          value={formatGroupedNumber(expenseTotal)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Contribution margin`)} ${t(
            `${translations.common}:total-short`
          )}`}
          unit="€"
          value={formatGroupedNumber(contributionMarginTotal)}
        />
      </Col>
      {/* Below 20 % red, above 30 % green, as on the rows */}
      <Col
        className={`sum-figure ${
          sellingPriceTotal === 0 ? '' : getMarginClass(contributionMarginRatio)
        }`}
      >
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Contribution margin`)} %`}
          unit="%"
          value={formatGroupedNumber(contributionMarginRatio)}
        />
      </Col>
    </Row>
  );
};

export default InstallationsSum;
